// Angular Files
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

// Other External Files
import { Subscription } from 'rxjs';

// Teller Online Library Files
import { TellerOnlineMessageService, TellerOnlineWindowService } from 'teller-online-libraries/shared';
import {
    TellerOnlineAppService,
    TellerOnlineErrorHandlerService,
    TellerOnlineSiteMetadataService
} from 'teller-online-libraries/core';

// Teller Online Files
import {
    InboundRedirectApiClient,
    InboundRedirectPaymentCompletionRequestDto,
    InboundRedirectPaymentCompletionResponseDto,
    InboundRedirectRequestResponseDto,
    InboundRedirectReturnCodeEnumDto,
    ParameterFormatEnumDto,
    PaymentMethodTypeEnumDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { CartService } from './cart.service';
import { AuthService } from './auth.service';

// Constants
const LOCAL_STORE_REDIRECT_SOURCE = 'inboundRedirectSource';
const LOCAL_STORE_QUERY_PARAMS = 'queryStringParamsFromRedirect';
const LOCAL_STORE_CONTEXT_UPDATING = 'inboundRedirectContextUpdate';
const LOCAL_STORE_COMMAND = 'inboundRedirectCommand';
const COMMAND_NAVIGATE = 'navigate';
const COMMAND_FINISH = 'finish';

@Injectable({
    providedIn: 'root'
})

export class InboundRedirectService implements OnDestroy {
    // Public variables
    public userFullName: string | undefined;

    public userEmail: string | undefined;

    public paymentAmount: number | undefined;

    public searchId: string | undefined;

    public cancelUrl: string | undefined;

    public errorUrl: string | undefined;

    public successUrl: string | undefined;

    public redirectSource: string | undefined;

    public redirectSourceId: number | undefined;

    public redirectSourceFriendlyName: string | undefined;

    public allowCartItemDetailsPage: boolean = false;

    public skipReceiptPage: boolean = false;

    public dontSendReceiptEmail: boolean = false;

    public processorTrackingId: string | undefined;

    public paymentMethodType: PaymentMethodTypeEnumDto | undefined;

    public showErrorDialog: boolean = false;

    public redirectInProgress: boolean = false;

    public requestTriggered: boolean = false;

    // Subscriptions
    private _queryParamsSubscription: Subscription | undefined;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private inboundRedirectApiClient: InboundRedirectApiClient,
                private windowService: TellerOnlineWindowService,
                private messageService: TellerOnlineMessageService,
                private cartService: CartService,
                private authService: AuthService,
                private siteMetadataService: TellerOnlineSiteMetadataService,
                private appService: TellerOnlineAppService,
                private errorHandlerService: TellerOnlineErrorHandlerService) {
        windowService.addEventListener('storage', this.onWindow_storage);
    }

    ngOnDestroy() {
        if (this._queryParamsSubscription) this._queryParamsSubscription.unsubscribe();
    }

    // A redirect was successfully verified, and the page was switched to the TOL context
    public get isInboundRedirect(): boolean {
        return this.siteMetadataService.tellerOnlineLiteEnabled
            && !!(this.windowService.getLocalStorageItem(LOCAL_STORE_REDIRECT_SOURCE)
                || this.redirectSource);
    }

    // Redirect verification is in progress
    public get isInboundRedirectContextVerification(): boolean {
        return !!this.windowService.getLocalStorageItem(LOCAL_STORE_CONTEXT_UPDATING);
    }

    // Redirect verification is in progress, OR the page was switched to the TOL context.
    // In both cases, we need to show UI as it is in the TOL context
    public get isInboundRedirectUIContext(): boolean {
        return this.isInboundRedirect || this.isInboundRedirectContextVerification
            || this.windowService.isTOL;
    }

    public async verifyAndSearch(redirectSource: string) {
        if (this.requestTriggered) {
            return;
        }

        try {
            this.requestTriggered = true;
            this.appService.triggerLoading('Verifying request...');

            const queryStringParams = this.route.snapshot.queryParams;
            const queryString = JSON.stringify(queryStringParams);
            this.windowService.setLocalStorageItem(LOCAL_STORE_CONTEXT_UPDATING, 'true');

            // make sure the user doesn't have any lingering params
            await this.removeRedirectParamsFromLocalStorage();

            if (!redirectSource) {
                await this.showErrorAndRedirectToHome('Redirect source could not be determined.');
            }

            const response = await this.verifyRedirectParameters(redirectSource, queryStringParams);
            if (response?.isValid) {
                // make sure the user is signed out
                if (this.siteMetadataService.authenticationEnabled) {
                    await this.authService.signOut();
                }

                // add querystring params to local store for later use
                await this.saveRedirectParams(response, queryString);

                this.windowService.removeLocalStorageItem(LOCAL_STORE_CONTEXT_UPDATING);

                const queryParams = {
                    searchType: response.searchTypeName,
                    searchValue: response.searchId,
                    paymentOptionType: response.paymentOptionType
                };
                response.additionalSearchValues.forEach(searchValue => {
                    queryParams[`additionalSearchValue_${searchValue.name}`] = searchValue.value;
                });
                // navigate to search page to automatically search based on received searchId
                await this.router.navigate(
                    [`/search/${response.pageId}`],
                    {
                        queryParams,
                        skipLocationChange: true
                    }
                );
            } else {
                this.windowService.removeLocalStorageItem(LOCAL_STORE_CONTEXT_UPDATING);
                await this.showErrorAndRedirectToHome(response.userErrorMessage);
            }
        } catch (e) {
            this.windowService.removeLocalStorageItem(LOCAL_STORE_CONTEXT_UPDATING);
            this.errorHandlerService.handleError(e, false);
            await this.showErrorAndRedirectToHome();
        }
    }

    public async reinitializeRequest(isCheckoutPage: boolean = false) {
        this.appService.triggerPageLoading();

        const redirectSource = this.windowService.getLocalStorageItem(
            LOCAL_STORE_REDIRECT_SOURCE
        );
        const queryStringJson = this.windowService.getLocalStorageItem(
            LOCAL_STORE_QUERY_PARAMS
        );

        const queryStringObj = JSON.parse(queryStringJson);
        const queryParams: Params = {};
        for (const key of Object.keys(queryStringObj)) {
            queryParams[key] = queryStringObj[key];
        }
        // If we are already on the checkout page
        // we want to save all our parameters but not do a full redirect
        if (isCheckoutPage) {
            const response = await this.verifyRedirectParameters(redirectSource, queryStringObj);
            this.saveRedirectParams(response, queryStringJson);
        } else {
            this.router.navigate(
                [`/TOL/${redirectSource}`],
                { queryParams }
            );
        }
    }

    public async removeRedirectParamsFromLocalStorage() {
        this.windowService.removeLocalStorageItem(LOCAL_STORE_REDIRECT_SOURCE);
        this.windowService.removeLocalStorageItem(LOCAL_STORE_QUERY_PARAMS);
    }

    public async promptForRedirectToSource() {
        let result = true;

        // Use success URL for the prompt (most common scenario)
        const redirectUrl = this.successUrl || this.cancelUrl || this.errorUrl;
        if (redirectUrl) {
            result = await this.messageService.prompt(`You are about to be redirected to external website<br/>${redirectUrl}<br/><br/>`
                + 'Would you like to proceed?', 'Redirecting to External Website');
        }

        if (result) {
            this.redirectToSource();
        }
    }

    public async redirectToSource() {
        this.completePaymentAndRedirect();
        this.notify(COMMAND_FINISH);
    }

    public async showErrorAndRedirect(additionalDetails: string = undefined) {
        // Indicate redirect is currently in progress to allow us to override default behavior.
        // Set this before cleaning up to ensure proper handling of cart reset
        this.redirectInProgress = true;
        // Clean up first so if the user leaves the page without going back to ACA,
        // the request is still considered complete.
        await this.cleanUpRequest();

        const response = await this.buildResponse(
            InboundRedirectReturnCodeEnumDto.Error,
            additionalDetails
        );

        if (this.showErrorDialog) {
            this.showError(additionalDetails, `<br/><br/>Click \`OK\` to return to ${this.redirectSourceFriendlyName}.`)
                .then(async () => {
                    await this.navigateToRedirectUrl(response);
                });
        } else {
            await this.navigateToRedirectUrl(response);
        }
    }

    public async cancelPaymentAndRedirect() {
        const response = await this.buildResponse(InboundRedirectReturnCodeEnumDto.Cancel, 'Payment was cancelled.');

        await this.navigateToRedirectUrl(response);

        this.notify(COMMAND_FINISH);
    }

    public finalizePayment(processorTrackingId: string, successRoute: string) {
        this.processorTrackingId = processorTrackingId;
        this.notify(COMMAND_NAVIGATE, successRoute);
    }

    //
    // Event Handlers
    //
    private onWindow_storage = async (e: StorageEvent) => {
        // To maintain TOL context between multiple browser tabs.
        // When a user completes a payment on one tab and is redirected to the receipt page,
        // we want him to be redirected to the receipt page on all the other opened tabs
        if (e.key === LOCAL_STORE_COMMAND && e.newValue) {
            const command = JSON.parse(e.newValue);
            switch (command.command) {
                case COMMAND_NAVIGATE:
                    this.router.navigate([command.arg]);
                    break;
                case COMMAND_FINISH:
                    this.redirectSource = null;
                    this.cleanUpRequest();
                    // When finishing a TOL payment, if the checkout page is opened in
                    // multiple tabs, navigate away to the main page to prevent showing
                    // the checkout page with an empty cart
                    if (this.router.url.startsWith('/checkout')) {
                        this.router.navigate(['/']);
                    }
                    break;
                default:
                    // Do nothing for unrecognized commands
            }
            this.windowService.removeLocalStorageItem(LOCAL_STORE_COMMAND);
        }
    };

    //
    // Private Methods
    //
    private async completePaymentAndRedirect() {
        const response = await this.buildResponse(InboundRedirectReturnCodeEnumDto.Success, 'Payment was successful.');
        await this.navigateToRedirectUrl(response);
    }

    private async showErrorAndRedirectToHome(additionalDetails: string = undefined) {
        // Clean up first so if the user leaves the page without being redirected
        // back to the source, the request is still considered complete.
        await this.cleanUpRequest();

        let message: string = '';
        if (additionalDetails) {
            message = '<br/><br/>';
        }
        message += 'Clicking `OK` will send you to Teller Online where you can search for your fees manually.';

        this.showError(additionalDetails, message)
            .then(async () => {
                // Replacing the url when navigating to the home page
                // to ensure that when a user clicks the browser's back button,
                // they are redirected back to a portal that triggered a redirect
                this.router.navigate(['/'], { replaceUrl: true });
            });
    }

    private async navigateToRedirectUrl(response: InboundRedirectPaymentCompletionResponseDto) {
        // Indicate redirect is currently in progress to allow us to override default behavior.
        this.redirectInProgress = true;

        await this.cleanUpRequest();

        // Show loader so the user can't attempt any action while being redirected
        this.appService.triggerPageLoading('Redirecting...');

        this.notify(COMMAND_FINISH);

        if (response) {
            if (response.redirectUrl) {
                switch (response.parameterFormat) {
                    case ParameterFormatEnumDto.None:
                        this.windowService.navigateExternalUrl(response.redirectUrl);
                        break;
                    case ParameterFormatEnumDto.QueryString:
                        this.windowService.navigateExternalUrl(`${response.redirectUrl}${response.redirectUrl.includes('?') ? '&' : '?'}${response.parameterString}`);
                        break;
                    // Support Json and Xml when needed
                    default:
                        throw new Error('Unsupported Parameter Format');
                }
            } else {
                // No redirect URL configured, navigate to home page
                this.router.navigate(['/']);
                // Reset the flag if we stay on TO page
                this.redirectInProgress = false;
                // Finally, reset redirect source to switch back to TO context
                this.redirectSource = null;
                // Stop the loading spinner since we're staying on the site
                this.appService.finishPageLoading();
            }
        }
    }

    private async cleanUpRequest() {
        await this.removeRedirectParamsFromLocalStorage();
        this.cartService.reset();
    }

    private async saveRedirectParams(
        response: InboundRedirectRequestResponseDto,
        queryString: string
    ) {
        if (response) {
            this.userFullName = response.fullName;
            this.userEmail = response.userEmail;
            this.paymentAmount = response.paymentAmount;
            this.searchId = response.searchId;
            this.paymentMethodType = response.paymentMethodType;
            this.showErrorDialog = response.showErrorDialog;
            this.cancelUrl = response.cancelUrl;
            this.errorUrl = response.errorUrl;
            this.successUrl = response.successUrl;
            this.redirectSourceFriendlyName = response.friendlyName;
            this.redirectSourceId = response.inboundRedirectSourceId;
            this.redirectSource = response.redirectSource;
            this.allowCartItemDetailsPage = response.allowCartItemDetailsPage;
            this.skipReceiptPage = response.skipReceiptPage;
            this.dontSendReceiptEmail = response.dontSendReceiptEmail;

            if (response.redirectSource) {
                // Also keep in local store in case we need to reinitialize the request
                this.windowService.setLocalStorageItem(
                    LOCAL_STORE_REDIRECT_SOURCE,
                    response.redirectSource
                );
            }

            if (queryString) {
                // Keep original querystring in local store
                // in case we need to reinitialize the request
                this.windowService.setLocalStorageItem(LOCAL_STORE_QUERY_PARAMS, queryString);
            }
        }
    }

    private showError(
        additionalDetails: string = undefined,
        buttonActionDescription: string = undefined
    ) {
        const additionalDetailMessage = additionalDetails
            ? `<b>Additional error details:</b><br/>${additionalDetails}`
            : '';

        const message = `An error occurred while processing your request.<br/><br/>${additionalDetailMessage}${buttonActionDescription}`;

        return this.messageService.alert(message, 'Error Processing Request');
    }

    private async verifyRedirectParameters(
        redirectSource: string,
        requestData
    ): Promise<InboundRedirectRequestResponseDto> {
        const result = await this.inboundRedirectApiClient.verifyRedirectParameters(
            redirectSource,
            requestData
        ).toPromise();
        return result;
    }

    private async buildResponse(
        returnCode: InboundRedirectReturnCodeEnumDto,
        customMessage: string
    ): Promise<InboundRedirectPaymentCompletionResponseDto | null> {
        const requestDto = new InboundRedirectPaymentCompletionRequestDto();

        requestDto.searchId = this.searchId;
        requestDto.redirectSource = this.redirectSource;
        requestDto.returnCode = returnCode;
        requestDto.userMessage = customMessage;
        requestDto.processorTransactionId = this.processorTrackingId;
        // Send all three URLs - backend will select the appropriate one based on returnCode
        requestDto.cancelUrl = this.cancelUrl;
        requestDto.errorUrl = this.errorUrl;
        requestDto.successUrl = this.successUrl;
        try {
            return await this.inboundRedirectApiClient.buildResponse(
                requestDto.redirectSource,
                requestDto
            ).toPromise();
        } catch (e) {
            this.errorHandlerService.handleError(e, false);
            await this.showErrorAndRedirectToHome();
            return null;
        }
    }

    // Send a notification to other tabs
    private notify(command: string, arg?: string) {
        this.windowService.setLocalStorageItem(
            LOCAL_STORE_COMMAND,
            JSON.stringify({ command, arg })
        );
    }
}
